import { useMemo } from 'react';

import { projectsRoutesConfig } from 'domains/projects/routes';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useJWTs } from 'domains/jwtToken/hooks/useJWTs';

export interface UseProjectFromParamsProps {
  skipFetching?: boolean;
}

/**
 * Resolves the project from the projectIndex url query param.
 * Also supports the legacy projectId param (the project API key)
 * to keep old links working.
 */
export const useProjectFromParams = ({
  skipFetching = false,
}: UseProjectFromParamsProps = {}) => {
  const { legacyProjectId, projectIndex } =
    projectsRoutesConfig.project.useParams();

  const { group } = useAccount();

  const {
    jwts,
    state: { isError, isSuccess },
  } = useJWTs({ group, skipFetching });

  const project = useMemo(() => {
    if (projectIndex !== undefined) {
      return jwts.find(jwt => jwt.index === projectIndex);
    }

    if (legacyProjectId) {
      return jwts.find(
        jwt =>
          jwt.userEndpointToken.toLowerCase() === legacyProjectId.toLowerCase(),
      );
    }

    return undefined;
  }, [jwts, legacyProjectId, projectIndex]);

  return {
    hasProjectParams: projectIndex !== undefined || Boolean(legacyProjectId),
    isLoaded: isError || isSuccess,
    project,
    projectIndex: project?.index ?? projectIndex,
    userEndpointToken: project?.userEndpointToken,
  };
};
